import { useCallback, useRef } from 'react';
import { useAppSelector } from '@/store/hooks';
import { selectUser } from '@/store/user/selectors';

let hubSpotInitialized = false;
let userIdentified = false;

/**
 * Reset HubSpot state. Only exported for testing purposes.
 */
export function _resetHubSpotForTesting(): void {
  hubSpotInitialized = false;
  userIdentified = false;
}

/**
 * HubSpot event names.
 * Events must be created in HubSpot's UI before they can be tracked.
 */
export const HUBSPOT_EVENT = {
  LOADED_PAGE_STUDIO: 'Loaded Page Studio',
  UPGRADE_MODAL_UPGRADE_CLICKED: 'Upgrade Modal Upgrade Clicked',
  UPGRADE_MODAL_DISMISSED: 'Upgrade Modal Dismissed',
} as const;

/**
 * Check if an admin is logged in as another user.
 * When this is true, we skip analytics tracking to avoid polluting data.
 */
export function isAdminLoggedInAsUser(): boolean {
  // Match exact cookie name with boundary: start-of-string or semicolon before "as_user_id"
  const match = document.cookie.match(/(^|;\s*)as_user_id=([^;]*)/);
  return match !== null && match[2] !== '';
}

/**
 * Initialize HubSpot tracking script.
 * This loads the HubSpot tracking code which enables:
 * - Page view tracking
 * - Form submission tracking
 * - Visitor identification
 *
 * Portal ID is injected at build time from environment variables.
 * For local development, set VITE_HUBSPOT_PORTAL_ID in .env.development.local
 */
export function setupHubSpot(): void {
  if (hubSpotInitialized) {
    return;
  }

  const portalId = import.meta.env.VITE_HUBSPOT_PORTAL_ID;

  if (!portalId) {
    return;
  }

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = `//js.hs-scripts.com/${portalId}.js`;

  const firstScript = document.getElementsByTagName('script')[0];
  firstScript.parentNode?.insertBefore(script, firstScript);

  hubSpotInitialized = true;
}

/**
 * Identify a user in HubSpot by email.
 * This associates the visitor's tracking cookie with their email address,
 * allowing HubSpot to tie events to a known contact.
 *
 * Must be called before trackHubSpotEvent for events to appear under a contact.
 * See: https://developers.hubspot.com/docs/api/events/tracking-code
 */
export function identifyHubSpotUser(email: string): void {
  if (!hubSpotInitialized || isAdminLoggedInAsUser()) {
    return;
  }

  if (!email) {
    return;
  }

  const hsq = ((window as Window & { _hsq?: unknown[][] })._hsq ??= []);
  hsq.push(['identify', { email }]);
}

/**
 * Track a custom behavioral event in HubSpot.
 * Uses HubSpot's _hsq tracking queue.
 *
 * Note: Events must be created in HubSpot's UI before they can be tracked.
 * Event names are automatically formatted with the portal ID prefix.
 */
export function trackHubSpotEvent(
  eventName: string,
  properties: Record<string, string | number | boolean> = {},
): void {
  if (!hubSpotInitialized || isAdminLoggedInAsUser()) {
    return;
  }

  const portalId = import.meta.env.VITE_HUBSPOT_PORTAL_ID;
  if (!portalId) {
    return;
  }

  // HubSpot requires event names to be formatted as pe{portal_id}_{event_name}
  // with snake_case and lowercase (matching @lp/events-js formatting)
  const formattedEventName = `pe${portalId}_${eventName.replace(/\s/g, '_').toLowerCase()}`;

  const hsq = ((window as Window & { _hsq?: unknown[][] })._hsq ??= []);
  hsq.push([
    'trackCustomBehavioralEvent',
    {
      name: formattedEventName,
      properties,
    },
  ]);
}

export type HubSpotTrackFunction = (
  eventName: string,
  properties?: Record<string, string | number | boolean>,
) => void;

/**
 * Hook to track HubSpot events with automatic user identification from Redux store.
 *
 * @example
 * ```tsx
 * import { useHubSpot, HUBSPOT_EVENT } from '@/hooks/useHubSpot';
 *
 * const track = useHubSpot();
 *
 * // Track event - user is automatically identified by email
 * track(HUBSPOT_EVENT.LOADED_PAGE_STUDIO);
 * ```
 */
export function useHubSpot(): HubSpotTrackFunction {
  const user = useAppSelector(selectUser);
  const hasIdentified = useRef(false);

  const track = useCallback(
    (eventName: string, properties: Record<string, string | number | boolean> = {}) => {
      if (!hubSpotInitialized || isAdminLoggedInAsUser()) {
        return;
      }

      // Identify user once per session if we have their email
      if (user?.email && !userIdentified && !hasIdentified.current) {
        hasIdentified.current = true;
        userIdentified = true;
        identifyHubSpotUser(user.email);
      }

      trackHubSpotEvent(eventName, properties);
    },
    [user],
  );

  return track;
}
